import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, getDocs, query, where, doc, updateDoc ,getDoc} from 'firebase/firestore';
import { db } from '../firebase';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Carousel } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Demo.css';

const Demo = () => {
  // State for demo environment
  const [demoType, setDemoType] = useState('');
  const [useCaseContext, setUseCaseContext] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [activeTab, setActiveTab] = useState('laws');
  
  // State for collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    laws: true,
    enforcement: true,
    penalty: true,
    insights: true
  });
  
  // State for upload visibility
  const [uploadVisibility, setUploadVisibility] = useState({
    tab3: false,
    tab4: false
  });
  
  // State for tab 1 - Research Analysis
  const [researchData, setResearchData] = useState(null);
  const [isResearchLoading, setIsResearchLoading] = useState(false);
  
  // State for tab 2 - High Level Issues
  const [selectedDemoImages, setSelectedDemoImages] = useState([]);
  const [availableDemoImages, setAvailableDemoImages] = useState([]);
  const [selectedImageForView, setSelectedImageForView] = useState(null);
  const [isImageViewOpen, setIsImageViewOpen] = useState(false);
  
  // State for expandable journey chart
  const [expandedJourneys, setExpandedJourneys] = useState({});
  
  // State for tab 3 - Uploads
  const [tab3Files, setTab3Files] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  
  // State for tab 4 - Report (URL input and iframe preview)
  // Removed tab4Url and isUrlInputVisible states as they're no longer needed
  
  // State for demo environment selection
  const [demoEnvironments, setDemoEnvironments] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState(null);
  const [isCreatingEnvironment, setIsCreatingEnvironment] = useState(false);
  const [newEnvironmentName, setNewEnvironmentName] = useState('');
  const [newEnvironmentDescription, setNewEnvironmentDescription] = useState('');
  
  // New state for session management
  const [availableSessions, setAvailableSessions] = useState([]);
  const [selectedSessions, setSelectedSessions] = useState([]);
  const [isUseCaseContextExpanded, setIsUseCaseContextExpanded] = useState(false);
  
  // State for image dropdown
  const [isImageDropdownOpen, setIsImageDropdownOpen] = useState(false);
  
  // Ref for image dropdown to handle click outside
  const imageDropdownRef = useRef(null);

  // New state for synthesis sub-tabs
  const [synthesisActiveSubTab, setSynthesisActiveSubTab] = useState('issues'); // 'issues' or 'examples'

  // New state for research analysis tabs
  const [researchActiveTab, setResearchActiveTab] = useState('insights'); // 'laws', 'enforcement', or 'insights'

  // New state for view mode
  const [isViewMode, setIsViewMode] = useState(false);

  // New state for research analysis edit mode
  const [isEditMode, setIsEditMode] = useState(false);

  // State for report iframe popup
  const [isIframePopupOpen, setIsIframePopupOpen] = useState(false);
  const [editableResearchData, setEditableResearchData] = useState(null);
  const [isSavingChanges, setIsSavingChanges] = useState(false);
  const [isEditControlsExpanded, setIsEditControlsExpanded] = useState(false);

  // Load demo environments on component mount
  useEffect(() => {
    loadDemoEnvironments();
    loadAvailableSessions();
    loadAvailableDemoImages();
  }, []);

  // Reload available demo images when selected environment changes
  useEffect(() => {
    if (selectedEnvironment) {
      // Debug: Log the selected environment's sessions
              if (selectedEnvironment.selectedSessions) {
          // Sync local selectedSessions state with environment sessions
          setSelectedSessionsSafely(selectedEnvironment.selectedSessions);
        }
      
      loadAvailableDemoImages();
    } else {
      // Clear images when no environment is selected
      setAvailableDemoImages([]);
      setSelectedSessionsSafely([]);
    }
  }, [selectedEnvironment]);

  // Handle clicks outside the image dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (imageDropdownRef.current && !imageDropdownRef.current.contains(event.target)) {
        setIsImageDropdownOpen(false);
      }
    };

    // Add event listener when dropdown is open
    if (isImageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isImageDropdownOpen]);

  // Load demo environments from Firebase
  const loadDemoEnvironments = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'demo-environments'));
      const environments = querySnapshot.docs.map(doc => {
        const envData = doc.data();
        
        // Create a copy to avoid mutating the original data
        const cleanEnvData = { ...envData };
        
        // Clean up any invalid session data in existing environments
        if (cleanEnvData.selectedSessions) {
          cleanEnvData.selectedSessions = getValidSessions(cleanEnvData.selectedSessions);
        }
        
        return {
          id: doc.id,
          ...cleanEnvData
        };
      });
      
      setDemoEnvironments(environments);
    } catch (error) {
      console.error('Error loading demo environments:', error);
      toast.error('Failed to load demo environments');
    }
  };

  // Load available sessions from Firebase (only those with images in demo-images collection)
  const loadAvailableSessions = async () => {
    try {
      // First, get all demo images to see which sessions have images
      const imagesSnapshot = await getDocs(collection(db, 'demo-images'));
      const images = imagesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Extract unique session IDs and names from images
      const sessionsWithImages = new Map();
      images.forEach(img => {
        // Only process images with valid sessionId
        if (img.sessionId && img.sessionId.trim() !== '') {
          sessionsWithImages.set(img.sessionId, {
            id: img.sessionId,
            sessionName: img.sessionName && img.sessionName.trim() !== '' ? img.sessionName : img.sessionId,
            description: `Has ${images.filter(i => i.sessionId === img.sessionId).length} images`
          });
        }
        // Only process images with valid sessionName that's different from sessionId
        if (img.sessionName && img.sessionName.trim() !== '' && img.sessionName !== img.sessionId) {
          sessionsWithImages.set(img.sessionName, {
            id: img.sessionName,
            sessionName: img.sessionName,
            description: `Has ${images.filter(i => i.sessionName === img.sessionName).length} images`
          });
        }
      });
      
      // Convert Map to array and filter out any sessions with undefined values
      const sessions = Array.from(sessionsWithImages.values()).filter(session => {
        return session.id && 
               session.id.trim() !== '' && 
               session.sessionName && 
               session.sessionName.trim() !== '' && 
               session.description;
      });
      
      console.log('Available sessions with images:', sessions);
      setAvailableSessions(sessions);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load sessions');
    }
  };

  // Load available demo images from Firebase
  const loadAvailableDemoImages = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'demo-images'));
      let images = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Filter images based on selected sessions if environment is selected
      if (selectedEnvironment && selectedEnvironment.selectedSessions && selectedEnvironment.selectedSessions.length > 0) {
        // Filter out any invalid sessions before processing
        const validSessions = getValidSessions(selectedEnvironment.selectedSessions);
        
        if (validSessions.length > 0) {
          images = images.filter(img => {
            // Check if image belongs to any of the valid selected sessions
            return validSessions.some(session => {
              // Match by session ID or session name
              return session.id === img.sessionId || 
                     session.sessionName === img.sessionName ||
                     session.id === img.sessionName; // Handle case where sessionName is used as ID
            });
          });
        } else {
          // If no valid sessions, show no images
          images = [];
        }
      } else {
        // If no environment is selected or no sessions are selected, show no images
        images = [];
      }
      
      setAvailableDemoImages(images);
    } catch (error) {
      console.error('Error loading demo images:', error);
      toast.error('Failed to load demo images');
    }
  };

  // Load previously saved demo data from environment
  const loadSavedDemo = async (environmentId) => {
    try {
      const environmentDoc = await getDoc(doc(db, 'demo-environments', environmentId));
      
      if (environmentDoc.exists()) {
        const envData = environmentDoc.data();
        
        // Load the saved demo data from environment
        if (envData.useCaseContext) setUseCaseContext(envData.useCaseContext);
        
        if (envData.analysisResults) {
          setAnalysisResults(envData.analysisResults);
          setResearchData(envData.analysisResults);
          // Initialize editable research data
          setEditableResearchData(JSON.parse(JSON.stringify(envData.analysisResults)));
        }
        
        if (envData.selectedImages) setSelectedDemoImages(envData.selectedImages);
        
        if (envData.tab3Files) setTab3Files(envData.tab3Files);
        
        // Removed tab4Url loading as it's no longer needed
        
        if (envData.selectedSessions) {
          // Filter out any invalid sessions before setting state
          const validSessions = getValidSessions(envData.selectedSessions);
          setSelectedSessionsSafely(validSessions);
        }
        
        // If environment has analysis results, automatically enter view mode
        if (envData.analysisResults) {
          setIsViewMode(true);
        }
        
        toast.success('Demo data loaded successfully!');
      }
    } catch (error) {
      console.error('Error loading saved demo:', error);
      toast.error('Failed to load saved demo data');
    }
  };

  // Create new demo environment
  const createDemoEnvironment = async () => {
    if (!newEnvironmentName.trim() || !newEnvironmentDescription.trim()) {
      toast.error('Please fill in all fields');
      return;
    }

    if (selectedSessions.length === 0) {
      toast.error('Please select at least one session');
      return;
    }

                // Validate and clean selected sessions before saving
        const cleanSelectedSessions = getValidSessions(selectedSessions);

    if (cleanSelectedSessions.length === 0) {
      toast.error('No valid sessions selected. Please select sessions with valid data.');
      return;
    }

    setIsCreatingEnvironment(true);
    try {
      const docRef = await addDoc(collection(db, 'demo-environments'), {
        name: newEnvironmentName.trim(),
        description: newEnvironmentDescription.trim(),
        selectedSessions: cleanSelectedSessions,
        createdAt: new Date(),
        status: 'active'
      });

      toast.success('Demo environment created successfully!');
      setNewEnvironmentName('');
      setNewEnvironmentDescription('');
      setSelectedSessionsSafely([]);
      setIsCreatingEnvironment(false);
      await loadDemoEnvironments(); // Wait for environments to load
      
                   // Automatically select the newly created environment
      const newEnv = {
        id: docRef.id,
        name: newEnvironmentName.trim(),
        description: newEnvironmentDescription.trim(),
        selectedSessions: cleanSelectedSessions,
        createdAt: new Date(),
        status: 'active'
      };
      
      setSelectedEnvironment(newEnv);
      
      // Also set the selected sessions in the local state
      setSelectedSessionsSafely(cleanSelectedSessions);
      
      // Don't reset form - let user continue with the flow
      // setUseCaseContext('');
      // setAnalysisResults(null);
      // setLawsData(null);
      // setSelectedDemoImages([]);
      // setTab3Files([]);
      
    } catch (error) {
      console.error('Error creating demo environment:', error);
      toast.error('Failed to create demo environment');
      setIsCreatingEnvironment(false);
    }
  };

  // Analyze use case context using Python API endpoint
  const analyzeUseCase = async () => {
    if (!useCaseContext.trim()) {
      toast.error('Please enter use case context');
      return;
    }

    setIsAnalyzing(true);
    try {
      // Call Python API endpoint for research analysis
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/get-research-analysis`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          context: useCaseContext
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      if (data.status === 'success' && data.data) {
        // Set the structured analysis results
        setAnalysisResults(data.data);
        setResearchData(data.data);
        // Initialize editable research data
        setEditableResearchData(JSON.parse(JSON.stringify(data.data)));
        toast.success('Research analysis completed successfully!');
      } else {
        throw new Error(data.message || 'Analysis failed');
      }
    } catch (error) {
      console.error('Error analyzing use case:', error);
      toast.error(`Failed to analyze use case: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  // Handle file uploads for tab 3 and 4
  const handleFileUpload = async (files, tabNumber) => {
    try {
      if (!selectedEnvironment) {
        toast.error('Please select a demo environment first');
        return;
      }

      // Create FormData with multiple files
      const formData = new FormData();
      
      // Add all files
      Array.from(files).forEach(file => {
        formData.append('files', file);
      });
      
      // Add metadata
      formData.append('environmentName', selectedEnvironment.name);
      formData.append('documentId', selectedEnvironment.id);
      formData.append('tabNumber', tabNumber.toString());
      
      // Upload files to Google Cloud Storage via Flask API
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/upload-files`, {
        method: 'POST',
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }
      
      const uploadResult = await response.json();
      
      if (uploadResult.success) {
        // Create file metadata objects (no need to store in separate collection)
        const uploadedFiles = [];
        
        for (const file of uploadResult.uploadedFiles) {
          // Create file metadata object
          const fileMetadata = {
            id: file.id, // Use the ID from the API response
            originalName: file.originalName,
            fileName: file.fileName,
            fileSize: file.fileSize,
            mimeType: file.mimeType,
            gcsPath: file.gcsPath,
            downloadURL: file.publicUrl,
            uploadedAt: new Date(),
            preview: file.mimeType.startsWith('image/') ? file.publicUrl : null
          };
          
          uploadedFiles.push(fileMetadata);
        }
        
        // Update tab3 files
        setTab3Files(prev => [...prev, ...uploadedFiles]);
        
        toast.success(`Successfully uploaded ${uploadedFiles.length} files to Google Cloud Storage`);
        
        // Log failed uploads if any
        if (uploadResult.failedUploads > 0) {
          console.warn('Some files failed to upload:', uploadResult.failedUploads);
          toast.warning(`${uploadResult.failedUploads} files failed to upload`);
        }
      } else {
        throw new Error(uploadResult.error || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error(`Failed to upload files: ${error.message}`);
    }
  };

  // Remove file from tab 3
  const removeFile = (fileId, tabNumber) => {
    setTab3Files(prev => prev.filter(f => f.id !== fileId));
  };

  // Select demo image for viewing
  const selectImageForView = (image) => {
    setSelectedImageForView(image);
    setIsImageViewOpen(true);
  };

  // Add state for image view mode
  const [imageViewMode, setImageViewMode] = useState('preview'); // 'preview' or 'detailed'
  const [imageLeftWidth, setImageLeftWidth] = useState(60); // percentage for resizable split
  const [isDraggingImageDivider, setIsDraggingImageDivider] = useState(false);

  // Add state for carousel preview system
  const [carouselPreviewOpen, setCarouselPreviewOpen] = useState(false);
  const [carouselFiles, setCarouselFiles] = useState([]);
  const [carouselActiveIndex, setCarouselActiveIndex] = useState(0);
  const [carouselPreviewType, setCarouselPreviewType] = useState(''); // 'tab3'
  const [carouselLoading, setCarouselLoading] = useState(false);

  // Enhanced file preview with carousel system for tab3
  const handleFilePreview = (file, tabNumber) => {
    if (file.mimeType.startsWith('image/')) {
      // For images, show in modal
      setSelectedImageForView({
        imageUrl: file.downloadURL,
        image_name: file.originalName,
        type: 'image'
      });
      setIsImageViewOpen(true);
    } else {
      // For other files (PDF, PowerPoint, Word), open carousel preview
      setCarouselFiles(tab3Files);
      setCarouselPreviewType('tab3');
      setCarouselActiveIndex(tab3Files.findIndex(f => f.id === file.id));
      setCarouselPreviewOpen(true);
    }
  };

  // Add functions for image view resizable divider
  const handleImageDividerMouseDown = (e) => {
    setIsDraggingImageDivider(true);
    e.preventDefault();
  };

  const handleImageDividerMouseMove = (e) => {
    if (!isDraggingImageDivider) return;
    
    const container = e.currentTarget.parentElement;
    const containerWidth = container.offsetWidth;
    const newLeftWidth = (e.clientX / containerWidth) * 100;
    
    // Constrain between 30% and 80%
    const constrainedWidth = Math.max(30, Math.min(80, newLeftWidth));
    setImageLeftWidth(constrainedWidth);
  };

  const handleImageDividerMouseUp = () => {
    setIsDraggingImageDivider(false);
  };

  // Helper function to clean data before saving to Firebase
  const cleanDataForFirebase = (data) => {
    if (data === null || data === undefined) return null;
    if (typeof data === 'object') {
      if (Array.isArray(data)) {
        return data.map(item => cleanDataForFirebase(item)).filter(item => item !== null);
      } else {
        const cleaned = {};
        Object.keys(data).forEach(key => {
          const cleanedValue = cleanDataForFirebase(data[key]);
          if (cleanedValue !== null) {
            cleaned[key] = cleanedValue;
          }
        });
        return cleaned;
      }
    }
    return data;
  };

  // Validation function to check if data is safe for Firebase
  const validateDataForFirebase = (data, fieldName = 'data') => {
    if (data === undefined) {
      console.error(`Validation failed: ${fieldName} is undefined`);
      return false;
    }
    if (data === null) {
      console.log(`Validation passed: ${fieldName} is null (allowed)`);
      return true;
    }
    if (typeof data === 'object') {
      if (Array.isArray(data)) {
        return data.every((item, index) => validateDataForFirebase(item, `${fieldName}[${index}]`));
      } else {
        return Object.keys(data).every(key => validateDataForFirebase(data[key], `${fieldName}.${key}`));
      }
    }
    return true;
  };

  /**
   * Utility function to safely get valid sessions
   * Filters out invalid or malformed session objects
   * @param {Array} sessions - Array of session objects to validate
   * @returns {Array} Array of valid session objects
   */
  const getValidSessions = (sessions) => {
    if (!Array.isArray(sessions)) {
      console.warn('getValidSessions: Input is not an array:', sessions);
      return [];
    }
    
    return sessions.filter(session => {
      // Check if session object exists and has required properties
      if (!session || typeof session !== 'object') {
        console.warn('getValidSessions: Invalid session object:', session);
        return false;
      }
      
      // Check if session has valid id and sessionName
      const hasValidId = session.id && typeof session.id === 'string' && session.id.trim() !== '';
      const hasValidName = session.sessionName && typeof session.sessionName === 'string' && session.sessionName.trim() !== '';
      
      if (!hasValidId || !hasValidName) {
        console.warn('getValidSessions: Session missing required properties:', {
          id: session.id,
          sessionName: session.sessionName,
          hasValidId,
          hasValidName
        });
        return false;
      }
      
      return true;
    });
  };



  /**
   * Safe wrapper for setting selected sessions
   * Handles both direct array values and function updates (React state setter pattern)
   * @param {Array|Function} sessionsOrFunction - Either an array of sessions or a function that returns an array
   */
  const setSelectedSessionsSafely = (sessionsOrFunction) => {
    if (typeof sessionsOrFunction === 'function') {
      // Handle function updates (React state setter pattern)
      setSelectedSessions(prevSessions => {
        const newSessions = sessionsOrFunction(prevSessions);
        
        if (Array.isArray(newSessions)) {
          const validSessions = getValidSessions(newSessions);
          return validSessions;
        } else {
          console.warn('Function returned non-array value:', newSessions);
          return getValidSessions(prevSessions);
        }
      });
    } else if (Array.isArray(sessionsOrFunction)) {
      // Handle direct array values
      const validSessions = getValidSessions(sessionsOrFunction);
      setSelectedSessions(validSessions);
    } else {
      console.warn('Attempted to set sessions with invalid value:', sessionsOrFunction);
      setSelectedSessions([]);
    }
  };

  /**
   * Render the Report tab with iframe preview and size controls
   * Displays the report interface with size options and popup functionality
   * @returns {JSX.Element} The Report tab component
   */
  const renderReportTab = () => {
    return (
      <div className="report-tab-container">
        <div className="iframe-preview-section">  
          <div className="iframe-container">
            <iframe
              src="https://report.fairpatterns.com/login"
              title="Report Interface"
              style={{
                width: '100%',
                height: '600px',
                border: '1px solid #ddd',
                borderRadius: '8px'
              }}
              allowFullScreen
              sandbox="allow-scripts allow-forms allow-popups allow-same-origin allow-top-navigation"
              referrerPolicy="no-referrer"
            />
          </div>
        </div>

        {/* Popup Button */}
        <div className="popup-controls">
          <button
            className="popup-button"
            onClick={() => setIsIframePopupOpen(true)}
            title="Open"
          >
            [ ]
          </button>
        </div>
      </div>
    );
  };

  // Save demo data to the environment itself
  const saveDemo = async () => {
    if (!selectedEnvironment) {
      toast.error('Please select a demo environment');
      return;
    }

    setIsSaving(true);
    try {
      // Clean and prepare data for Firebase
      const cleanSelectedImages = selectedDemoImages
        .filter(img => img && img.id && typeof img.id === 'string' && img.id.trim() !== '') // Filter out any null/undefined/invalid images
        .map(img => ({
          id: img.id || '',
          image_name: img.image_name || '',
          sessionName: img.sessionName || '',
          sessionId: img.sessionId || '',
          journeyName: img.journeyName || '',
          pageName: img.pageName || '',
          imageUrl: img.imageUrl || '',
          url: img.url || '',
          response: img.response || '',
          confirmedOutcomes: img.confirmedOutcomes || '',
          createdAt: img.createdAt || new Date()
        }));

      let cleanSelectedSessions = getValidSessions(selectedSessions);
      
      // If local selectedSessions is empty, try to use environment sessions
      if (cleanSelectedSessions.length === 0 && selectedEnvironment && selectedEnvironment.selectedSessions) {
        const envSessions = getValidSessions(selectedEnvironment.selectedSessions);
        if (envSessions.length > 0) {
          cleanSelectedSessions = envSessions;
        }
      }
      
      // Additional safety check: ensure all session objects have valid properties
      const finalCleanSessions = cleanSelectedSessions.filter(session => {
        const isValid = session && 
                       session.id && 
                       typeof session.id === 'string' && 
                       session.id.trim() !== '' && 
                       session.sessionName && 
                       typeof session.sessionName === 'string' && 
                       session.sessionName.trim() !== '';
        
        if (!isValid) {
          console.warn('Filtering out invalid session:', session);
        }
        
        return isValid;
      });

      const cleanTab3Files = tab3Files
        .filter(f => f && f.id && typeof f.id === 'string' && f.id.trim() !== '') // Filter out any null/undefined/invalid files
        .map(f => ({ 
          id: f.id || '',
          originalName: f.originalName || '', 
          mimeType: f.mimeType || '', 
          fileSize: f.fileSize || 0, 
          fileName: f.fileName || '',
          downloadURL: f.downloadURL || '',
          gcsPath: f.gcsPath || '',
          uploadedAt: f.uploadedAt || new Date()
        }));

      // Update the environment document with all demo data
      const environmentRef = doc(db, 'demo-environments', selectedEnvironment.id);
      
      const updateData = {
        useCaseContext: (useCaseContext && typeof useCaseContext === 'string') ? useCaseContext : '',
        analysisResults: analysisResults ? cleanDataForFirebase(analysisResults) : null,
        selectedImages: cleanDataForFirebase(cleanSelectedImages),
        selectedSessions: finalCleanSessions, // Don't call cleanDataForFirebase again - sessions are already cleaned
        tab3Files: cleanDataForFirebase(cleanTab3Files),
        // Removed tab4Url as it's no longer needed
        lastUpdated: new Date()
      };

      // Validate all data before saving to Firebase
      if (!validateDataForFirebase(updateData, 'updateData')) {
        throw new Error('Data validation failed. Cannot save to Firebase.');
      }
      
      // Additional safety check: ensure no undefined values in critical fields
      const criticalFields = ['selectedSessions', 'selectedImages', 'tab3Files'];
      for (const field of criticalFields) {
        if (updateData[field] && Array.isArray(updateData[field])) {
          const hasUndefined = updateData[field].some(item => 
            item === undefined || 
            (typeof item === 'object' && Object.values(item).some(val => val === undefined))
          );
          if (hasUndefined) {
            throw new Error(`Critical field ${field} contains undefined values. Cannot save to Firebase.`);
          }
        }
      }
      
      // Final validation: ensure all required fields have valid values
      if (!updateData.useCaseContext || typeof updateData.useCaseContext !== 'string') {
        throw new Error('useCaseContext must be a valid string');
      }
      
      if (!Array.isArray(updateData.selectedSessions)) {
        throw new Error('selectedSessions must be an array');
      }
      
      if (!Array.isArray(updateData.selectedImages)) {
        throw new Error('selectedImages must be an array');
      }
      
      if (!Array.isArray(updateData.tab3Files)) {
        throw new Error('tab3Files must be an array');
      }
      
      // Additional validation: ensure all arrays contain valid objects
      if (updateData.selectedSessions.length > 0) {
        const invalidSessions = updateData.selectedSessions.filter(session => 
          !session || !session.id || !session.sessionName
        );
        if (invalidSessions.length > 0) {
          throw new Error(`selectedSessions contains ${invalidSessions.length} invalid session objects`);
        }
      }
      
      if (updateData.selectedImages.length > 0) {
        const invalidImages = updateData.selectedImages.filter(img => 
          !img || !img.id
        );
        if (invalidImages.length > 0) {
          throw new Error(`selectedImages contains ${invalidImages.length} invalid image objects`);
        }
      }
      
      await updateDoc(environmentRef, updateData);

      toast.success('Demo saved successfully!');
      
      // Reload the environments to get the updated data
      await loadDemoEnvironments();
      
      // Don't reset form - keep everything for user to continue working
      // setUseCaseContext('');
      // setAnalysisResults(null);
      // setSelectedDemoImages([]);
      // setTab3Files([]);
    } catch (error) {
      console.error('Error saving demo:', error);
      
      // Provide more specific error messages
      if (error.message === 'Data validation failed. Cannot save to Firebase.') {
        toast.error('Data validation failed. Please check the console for details.');
      } else if (error.code === 'firestore/invalid-argument') {
        toast.error('Invalid data detected. Please refresh and try again.');
      } else {
        toast.error(`Failed to save demo: ${error.message}`);
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Toggle section expansion
  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  // Toggle upload visibility
  const toggleUploadVisibility = (tabNumber) => {
    const tabKey = `tab${tabNumber}`;
    setUploadVisibility(prev => ({
      ...prev,
      [tabKey]: !prev[tabKey]
    }));
  };

  // Render research analysis tables for structured data
  const renderResearchCharts = () => {
    if (!researchData) return null;

    const { laws, enforcement_trends, penalty, insights_for_company } = researchData;
    const dataToRender = isEditMode ? editableResearchData : researchData;

    return (
      <div className="research-analysis-container">

        {/* Research Analysis Tabs */}
        {laws && laws.length > 0 && enforcement_trends && enforcement_trends.length > 0 && (
          <div className="analysis-table-section">
            <div className="synthesis-sub-tabs">
              <div className="sub-tab-slider">
                <button 
                  className={`sub-tab-button ${researchActiveTab === 'laws' ? 'active' : ''}`}
                  onClick={() => setResearchActiveTab('laws')}
                >
                  Laws & Regulations
                </button>
                <button 
                  className={`sub-tab-button ${researchActiveTab === 'enforcement' ? 'active' : ''}`}
                  onClick={() => setResearchActiveTab('enforcement')}
                >
                  Enforcement Trends
                </button>
                <button 
                  className={`sub-tab-button ${researchActiveTab === 'insights' ? 'active' : ''}`}
                  onClick={() => setResearchActiveTab('insights')}
                >
                  Insights
                </button>
              </div>
              
              <div className="sub-tab-content">
                {/* Laws & Regulations Tab */}
                {researchActiveTab === 'laws' && (
                  <div className="high-level-issues">
                    {isEditMode && (
                      <div className="table-edit-controls">
                        <button 
                          className="add-row-btn"
                          onClick={addLawRow}
                        >
                          ➕ Add Law
                        </button>
                      </div>
                    )}
                    <div className="table-container">
                      <table className="analysis-table">
                        <thead>
                          <tr>
                            <th>Law/Regulation Name</th>
                            <th>Jurisdiction</th>
                            <th>Key Provisions</th>
                            <th>Typical Violations</th>
                            <th>Penalty Range</th>
                            {isEditMode && <th>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(dataToRender?.laws || laws).map((law, index) => (
                            <tr key={index}>
                              <td className="law-name">
                                {isEditMode ? (
                                  <input
                                    type="text"
                                    className="edit-input"
                                    value={law.name}
                                    onChange={(e) => updateLawField(index, 'name', e.target.value)}
                                  />
                                ) : (
                                  law.name
                                )}
                              </td>
                              <td className="jurisdiction">
                                {isEditMode ? (
                                  <input
                                    type="text"
                                    className="edit-input"
                                    value={law.jurisdiction}
                                    onChange={(e) => updateLawField(index, 'jurisdiction', e.target.value)}
                                  />
                                ) : (
                                  law.jurisdiction
                                )}
                              </td>
                              <td className="provisions">
                                <ul>
                                  {law.key_provisions.map((provision, idx) => (
                                    <li key={idx} className="editable-list-item">
                                      {isEditMode ? (
                                        <div className="edit-list-item">
                                          <textarea
                                            className="edit-textarea"
                                            value={provision}
                                            onChange={(e) => updateProvisionText(index, idx, e.target.value)}
                                            rows="2"
                                          />
                                          <button
                                            className="remove-item-btn"
                                            onClick={() => removeProvisionFromLaw(index, idx)}
                                            title="Remove provision"
                                          >
                                            ❌
                                          </button>
                                        </div>
                                      ) : (
                                        provision
                                      )}
                                    </li>
                                  ))}
                                  {isEditMode && (
                                    <li>
                                      <button
                                        className="add-item-btn"
                                        onClick={() => addProvisionToLaw(index)}
                                      >
                                        ➕ Add Provision
                                      </button>
                                    </li>
                                  )}
                                </ul>
                              </td>
                              <td className="violations">
                                <ul>
                                  {law.typical_violations.map((violation, idx) => (
                                    <li key={idx} className="editable-list-item">
                                      {isEditMode ? (
                                        <div className="edit-list-item">
                                          <textarea
                                            className="edit-textarea"
                                            value={violation}
                                            onChange={(e) => updateViolationText(index, idx, e.target.value)}
                                            rows="2"
                                          />
                                          <button
                                            className="remove-item-btn"
                                            onClick={() => removeViolationFromLaw(index, idx)}
                                            title="Remove violation"
                                          >
                                            ❌
                                          </button>
                                        </div>
                                      ) : (
                                        violation
                                      )}
                                    </li>
                                  ))}
                                  {isEditMode && (
                                    <li>
                                      <button
                                        className="add-item-btn"
                                        onClick={() => addViolationToLaw(index)}
                                      >
                                        ➕ Add Violation
                                      </button>
                                    </li>
                                  )}
                                </ul>
                              </td>
                              <td className="penalty">
                                {isEditMode ? (
                                  <textarea
                                    className="edit-textarea"
                                    value={law.penalty_range}
                                    onChange={(e) => updateLawField(index, 'penalty_range', e.target.value)}
                                    rows="2"
                                  />
                                ) : (
                                  law.penalty_range
                                )}
                              </td>
                              {isEditMode && (
                                <td className="actions">
                                  <button
                                    className="remove-row-btn"
                                    onClick={() => removeLawRow(index)}
                                    title="Remove law"
                                  >
                                    🗑️
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Enforcement Trends Tab */}
                {researchActiveTab === 'enforcement' && (
                  <div className="high-level-issues">
                    {isEditMode && (
                      <div className="table-edit-controls">
                        <button 
                          className="add-row-btn"
                          onClick={addEnforcementTrendRow}
                        >
                          ➕ Add Enforcement Trend
                        </button>
                      </div>
                    )}
                    <div className="table-container">
                      <table className="analysis-table">
                        <thead>
                          <tr>
                            <th>Jurisdiction</th>
                            <th>Authority</th>
                            <th>Emerging Enforcement Focus</th>
                            <th>Recent Case Examples</th>
                            <th>Risk Level</th>
                            {isEditMode && <th>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {(dataToRender?.enforcement_trends || enforcement_trends).map((trend, index) => (
                            <tr key={index}>
                              <td className="jurisdiction">
                                {isEditMode ? (
                                  <input
                                    type="text"
                                    className="edit-input"
                                    value={trend.jurisdiction}
                                    onChange={(e) => updateEnforcementTrendField(index, 'jurisdiction', e.target.value)}
                                  />
                                ) : (
                                  trend.jurisdiction
                                )}
                              </td>
                              <td className="authority">
                                {isEditMode ? (
                                  <input
                                    type="text"
                                    className="edit-input"
                                    value={trend.authority}
                                    onChange={(e) => updateEnforcementTrendField(index, 'authority', e.target.value)}
                                  />
                                ) : (
                                  trend.authority
                                )}
                              </td>
                              <td className="focus">
                                <ul>
                                  {trend.emerging_enforcement_focus.map((focus, idx) => (
                                    <li key={idx} className="editable-list-item">
                                      {isEditMode ? (
                                        <div className="edit-list-item">
                                          <textarea
                                            className="edit-textarea"
                                            value={focus}
                                            onChange={(e) => {
                                              const newFocus = [...trend.emerging_enforcement_focus];
                                              newFocus[idx] = e.target.value;
                                              updateEnforcementTrendField(index, 'emerging_enforcement_focus', newFocus);
                                            }}
                                            rows="2"
                                          />
                                          <button
                                            className="remove-item-btn"
                                            onClick={() => {
                                              const newFocus = trend.emerging_enforcement_focus.filter((_, i) => i !== idx);
                                              updateEnforcementTrendField(index, 'emerging_enforcement_focus', newFocus);
                                            }}
                                            title="Remove focus area"
                                          >
                                            ❌
                                          </button>
                                        </div>
                                      ) : (
                                        focus
                                      )}
                                    </li>
                                  ))}
                                  {isEditMode && (
                                    <li>
                                      <button
                                        className="add-item-btn"
                                        onClick={() => {
                                          const newFocus = [...trend.emerging_enforcement_focus, "New focus area"];
                                          updateEnforcementTrendField(index, 'emerging_enforcement_focus', newFocus);
                                        }}
                                      >
                                        ➕ Add Focus Area
                                      </button>
                                    </li>
                                  )}
                                </ul>
                              </td>
                              <td className="cases">
                                <ul>
                                  {trend.recent_case_examples
                                    .filter(case_example => 
                                      // Only show cases that are validated as true
                                      case_example.validation && case_example.validation.is_true === true
                                    )
                                    .map((case_example, idx) => (
                                    <li key={idx} className="editable-list-item">
                                      {isEditMode ? (
                                        <div className="edit-case-item">
                                          <div className="case-edit-fields">
                                            {/* Original case fields */}
                                            <input
                                              type="text"
                                              className="edit-input"
                                              placeholder="Case name"
                                              value={case_example.case}
                                              onChange={(e) => {
                                                const newCases = [...trend.recent_case_examples];
                                                newCases[idx] = { ...newCases[idx], case: e.target.value };
                                                updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                              }}
                                            />
                                            <input
                                              type="number"
                                              className="edit-input"
                                              placeholder="Year"
                                              value={case_example.year}
                                              onChange={(e) => {
                                                const newCases = [...trend.recent_case_examples];
                                                newCases[idx] = { ...newCases[idx], year: parseInt(e.target.value) };
                                                updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                              }}
                                            />
                                            <input
                                              type="text"
                                              className="edit-input"
                                              placeholder="Amount"
                                              value={case_example.amount || ''}
                                              onChange={(e) => {
                                                const newCases = [...trend.recent_case_examples];
                                                newCases[idx] = { ...newCases[idx], amount: e.target.value };
                                                updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                              }}
                                            />
                                            <textarea
                                              className="edit-textarea"
                                              placeholder="Details"
                                              value={case_example.details || ''}
                                              onChange={(e) => {
                                                const newCases = [...trend.recent_case_examples];
                                                newCases[idx] = { ...newCases[idx], details: e.target.value };
                                                updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                              }}
                                              rows="2"
                                            />
                                            
                                            {/* Validation fields */}
                                            <div className="validation-section">
                                              <h4>Validation Settings</h4>
                                              
                                              {/* Is True checkbox */}
                                              <div className="validation-field">
                                                <label>
                                                  <input
                                                    type="checkbox"
                                                    checked={case_example.validation?.is_true || false}
                                                    onChange={(e) => {
                                                      const newCases = [...trend.recent_case_examples];
                                                      newCases[idx] = {
                                                        ...newCases[idx],
                                                        validation: {
                                                          ...newCases[idx].validation,
                                                          is_true: e.target.checked,
                                                          validated_at: new Date().toISOString()
                                                        }
                                                      };
                                                      updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                                    }}
                                                  />
                                                  Is Validated as True
                                                </label>
                                              </div>
                                              
                                              {/* Summary field */}
                                              <textarea
                                                className="edit-textarea"
                                                placeholder="Validation Summary"
                                                value={case_example.validation?.summary || ''}
                                                onChange={(e) => {
                                                  const newCases = [...trend.recent_case_examples];
                                                  newCases[idx] = {
                                                    ...newCases[idx],
                                                    validation: {
                                                      ...newCases[idx].validation,
                                                      summary: e.target.value
                                                    }
                                                  };
                                                  updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                                }}
                                                rows="3"
                                              />
                                              
                                              {/* Sources section */}
                                              <div className="sources-section">
                                                <h5>Sources</h5>
                                                {case_example.validation?.sources?.map((source, sourceIdx) => (
                                                  <div key={sourceIdx} className="source-edit-item">
                                                    <input
                                                      type="text"
                                                      className="edit-input"
                                                      placeholder="Source Title"
                                                      value={source.title || ''}
                                                      onChange={(e) => {
                                                        const newCases = [...trend.recent_case_examples];
                                                        const newSources = [...(newCases[idx].validation?.sources || [])];
                                                        newSources[sourceIdx] = { ...newSources[sourceIdx], title: e.target.value };
                                                        newCases[idx] = {
                                                          ...newCases[idx],
                                                          validation: {
                                                            ...newCases[idx].validation,
                                                            sources: newSources
                                                          }
                                                        };
                                                        updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                                      }}
                                                    />
                                                    <input
                                                      type="url"
                                                      className="edit-input"
                                                      placeholder="Source URL"
                                                      value={source.url || ''}
                                                      onChange={(e) => {
                                                        const newCases = [...trend.recent_case_examples];
                                                        const newSources = [...(newCases[idx].validation?.sources || [])];
                                                        newSources[sourceIdx] = { ...newSources[sourceIdx], url: e.target.value };
                                                        newCases[idx] = {
                                                          ...newCases[idx],
                                                          validation: {
                                                            ...newCases[idx].validation,
                                                            sources: newSources
                                                          }
                                                        };
                                                        updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                                      }}
                                                    />
                                                    <button
                                                      className="remove-source-btn"
                                                      onClick={() => {
                                                        const newCases = [...trend.recent_case_examples];
                                                        const newSources = (newCases[idx].validation?.sources || []).filter((_, i) => i !== sourceIdx);
                                                        newCases[idx] = {
                                                          ...newCases[idx],
                                                          validation: {
                                                            ...newCases[idx].validation,
                                                            sources: newSources
                                                          }
                                                        };
                                                        updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                                      }}
                                                      title="Remove source"
                                                    >
                                                      ❌
                                                    </button>
                                                  </div>
                                                ))}
                                                <button
                                                  className="add-source-btn"
                                                  onClick={() => {
                                                    const newCases = [...trend.recent_case_examples];
                                                    const newSources = [...(newCases[idx].validation?.sources || []), {
                                                      title: "New Source",
                                                      url: "https://example.com"
                                                    }];
                                                    newCases[idx] = {
                                                      ...newCases[idx],
                                                      validation: {
                                                        ...newCases[idx].validation,
                                                        sources: newSources
                                                      }
                                                    };
                                                    updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                                  }}
                                                >
                                                  ➕ Add Source
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                          <button
                                            className="remove-item-btn"
                                            onClick={() => {
                                              const newCases = trend.recent_case_examples.filter((_, i) => i !== idx);
                                              updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                            }}
                                            title="Remove case"
                                          >
                                            ❌
                                          </button>
                                        </div>
                                      ) : (
                                        <>
                                          <div className="case-main">
                                            {/* Display summary instead of case name */}
                                            <span className="case-summary">
                                              {case_example.validation && case_example.validation.summary 
                                                ? case_example.validation.summary 
                                                : case_example.case
                                              } ({case_example.year})
                                            </span>
                                          </div>
                                          {case_example.amount && (
                                            <div className="case-amount">💰 {case_example.amount}</div>
                                          )}
                                          {case_example.details && (
                                            <div className="case-details">📋 {case_example.details}</div>
                                          )}
                                          {/* Display numbered source links */}
                                          {case_example.validation && case_example.validation.sources && case_example.validation.sources.length > 0 && (
                                            <div className="case-sources">
                                              <span className="sources-label">Sources: </span>
                                              {case_example.validation.sources.map((source, sourceIdx) => (
                                                <span key={sourceIdx} className="source-link">
                                                  <a
                                                    href={ensureValidUrl(source.url)}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    title={source.title || source.url}
                                                  >
                                                    [{sourceIdx + 1}]
                                                  </a>
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </li>
                                  ))}
                                  {isEditMode && (
                                    <li>
                                      <button
                                        className="add-item-btn"
                                        onClick={() => {
                                          const newCase = {
                                            case: "New Case",
                                            year: new Date().getFullYear(),
                                            amount: "Amount TBD",
                                            details: "Case details",
                                            source_link: "",
                                            validation: {
                                              is_true: true,
                                              sources: [
                                                {
                                                  title: "Source Title",
                                                  url: "https://example.com"
                                                }
                                              ],
                                              summary: "Case summary",
                                              validated_at: new Date().toISOString()
                                            }
                                          };
                                          const newCases = [...trend.recent_case_examples, newCase];
                                          updateEnforcementTrendField(index, 'recent_case_examples', newCases);
                                        }}
                                      >
                                        ➕ Add Case
                                      </button>
                                    </li>
                                  )}
                                </ul>
                              </td>
                              <td className="risk-level">
                                {isEditMode ? (
                                  <select
                                    className="edit-select"
                                    value={trend.risk_level}
                                    onChange={(e) => updateEnforcementTrendField(index, 'risk_level', e.target.value)}
                                  >
                                    <option value="Low">Low</option>
                                    <option value="Medium">Medium</option>
                                    <option value="Medium-High">Medium-High</option>
                                    <option value="High">High</option>
                                  </select>
                                ) : (
                                  <span className={`risk-badge risk-${trend.risk_level.toLowerCase().replace('-', '-')}`}>
                                    {trend.risk_level}
                                  </span>
                                )}
                              </td>
                              {isEditMode && (
                                <td className="actions">
                                  <button
                                    className="remove-row-btn"
                                    onClick={() => removeEnforcementTrendRow(index)}
                                    title="Remove enforcement trend"
                                  >
                                    🗑️
                                  </button>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Insights Tab */}
                {researchActiveTab === 'insights' && (
                  <div className="high-level-issues">
                    {isEditMode && (
                      <div className="insights-edit-controls">
                        <button 
                          className="add-insight-btn"
                          onClick={addInsight}
                        >
                          ➕ Add Insight
                        </button>
                      </div>
                    )}
                    <div className="chart-container">
                      {/* Insights Display */}
                      {(dataToRender?.insights_for_company?.key_insights || insights_for_company?.key_insights) && 
                       (dataToRender?.insights_for_company?.key_insights || insights_for_company?.key_insights).length > 0 ? (
                        <div className="insights-container">
                          {(dataToRender?.insights_for_company?.key_insights || insights_for_company?.key_insights).map((insight, idx) => (
                            <div key={idx} className="insight-item editable-insight">
                              <span className="insight-icon">💡</span>
                              {isEditMode ? (
                                <div className="edit-insight-container">
                                  <textarea
                                    className="edit-insight-textarea"
                                    value={insight}
                                    onChange={(e) => updateInsightText(idx, e.target.value)}
                                    rows="3"
                                    placeholder="Enter insight text..."
                                  />
                                  <button
                                    className="remove-insight-btn"
                                    onClick={() => removeInsight(idx)}
                                    title="Remove insight"
                                  >
                                    ❌
                                  </button>
                                </div>
                              ) : (
                                <span className="insight-text">{insight}</span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="empty-chart-state">
                          <div className="empty-icon">📊</div>
                          <h5>No Insights Available</h5>
                          <p>
                            {isEditMode 
                              ? "Click 'Add Insight' to add new insights, or run an analysis to generate insights automatically."
                              : "Run an analysis to generate key insights for your company."
                            }
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Fallback for string responses */}
        {researchData.rawResponse && !laws && !enforcement_trends && (
          <div className="string-response-container">
            <h4>Research Analysis Results</h4>
            <div className="response-content">
              <pre className="formatted-response">{researchData.rawResponse}</pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render demo image selection
  const renderImageSelection = () => {
    return (
      <div className="image-selection">
        {/* <h4>Select Demo Images</h4> */}
        
        {/* Image Selection - Single Add Button with Icon */}
        <div className="image-add-section">
          <button 
            className="add-image-btn"
            onClick={() => setIsImageDropdownOpen(!isImageDropdownOpen)}
            title="Add images to selection"
          >
            <span className="add-text">Select Images</span>
          </button>
          
          {/* Collapsible Image Dropdown */}
          {isImageDropdownOpen && (
            <div className="image-dropdown-container" ref={imageDropdownRef}>
              <div className="dropdown-header">
                <h5>Select Images ({availableDemoImages.length} available)</h5>
                <button 
                  className="close-dropdown-btn"
                  onClick={() => setIsImageDropdownOpen(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="image-selection-grid">
                {availableDemoImages.map((image) => {
                  const isSelected = selectedDemoImages.some(selected => selected.id === image.id);
                  const isVideo = isTikTokUrl(image.imageUrl);
                  return (
                    <div 
                      key={image.id} 
                      className={`image-selection-grid-item ${isSelected ? 'selected' : ''}`}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedDemoImages(prev => prev.filter(img => img.id !== image.id));
                        } else {
                          setSelectedDemoImages(prev => [...prev, image]);
                        }
                      }}
                    >
                      <div className="image-preview-container">
                        {isVideo ? (
                          <div style={{
                            width: '100%',
                            height: '100%',
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: '8px'
                          }}>
                            <iframe
                              src={getTikTokEmbedUrl(image.imageUrl)}
                              title={image.image_name}
                              style={{
                                width: '100%',
                                height: '100%',
                                border: 'none',
                                pointerEvents: 'none'
                              }}
                              scrolling="no"
                            />
                          </div>
                        ) : (
                          <img 
                            src={image.imageUrl} 
                            alt={image.image_name} 
                            className="image-preview"
                          />
                        )}
                        <div className="image-checkbox-overlay">
                          <div className="selection-checkbox">
                            {isSelected ? '☑️' : '☐'}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {availableDemoImages.length === 0 && (
                <div className="no-images-message">
                  <p>No images available for the selected sessions.</p>
                  <p>Please select sessions with images when creating the environment.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Selected Images Display */}
        {selectedDemoImages.length > 0 && (
          <div className="selected-images-section">
            <h5>Selected Images ({selectedDemoImages.length})</h5>
            <div className="selected-images-grid">
              {selectedDemoImages.map((image) => {
                const isVideo = isTikTokUrl(image.imageUrl);
                return (
                  <div key={image.id} className="selected-image-item">
                    <div className="image-preview-container">
                      {isVideo ? (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          position: 'relative',
                          overflow: 'hidden',
                          borderRadius: '8px'
                        }}>
                          <iframe
                            src={getTikTokEmbedUrl(image.imageUrl)}
                            title={image.image_name}
                            style={{
                              width: '100%',
                              height: '100%',
                              border: 'none',
                              pointerEvents: 'none'
                            }}
                            scrolling="no"
                          />
                        </div>
                      ) : (
                        <img src={image.imageUrl} alt={image.image_name} />
                      )}
                      <div className="image-overlay">
                        <button 
                          className="view-button"
                          onClick={() => selectImageForView(image)}
                        >
                          👁️ View
                        </button>
                      </div>
                    </div>
                    <div className="image-info">
                      <div className="image-name">{image.image_name}</div>
                      <div className="image-details">
                        <span className="session-name">{image.sessionName}</span>
                        <span className="journey-name">{image.journeyName}</span>
                      </div>
                    </div>
                    <button 
                      className="remove-button"
                      onClick={() => setSelectedDemoImages(prev => prev.filter(img => img.id !== image.id))}
                      title="Remove image"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render file upload section
  const renderFileUpload = (tabNumber, files, title) => {
    const tabKey = `tab${tabNumber}`;
    const isVisible = uploadVisibility[tabKey];
    
    return (
      <div className="file-upload-section">
        <div className="upload-section-header">
          <h4>{title}</h4>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {files.length > 0 && (
              <button 
                className="preview-all-btn"
                onClick={() => openCarouselPreview(tabNumber)}
                disabled={carouselLoading}
                title="Preview all files in carousel"
              >
                {carouselLoading ? '⏳ Loading...' : `🖼️ Preview All (${files.length})`}
              </button>
            )}
            <button 
              className="add-upload-btn"
              onClick={() => toggleUploadVisibility(tabNumber)}
              title={isVisible ? 'Hide upload area' : 'Show upload area'}
            >
              {isVisible ? '−' : '+'}
            </button>
          </div>
        </div>
        
        {isVisible && (
          <div className="upload-area">
            <input
              type="file"
              multiple
              accept="image/*,.ppt,.pptx,.pdf,.doc,.docx"
              onChange={(e) => handleFileUpload(e.target.files, tabNumber)}
              id={`file-upload-${tabNumber}`}
              className="file-input"
            />
            <label htmlFor={`file-upload-${tabNumber}`} className="upload-label">
              <div className="upload-icon">📁</div>
              <div className="upload-text">
                <strong>Click to upload files</strong>
                <span>or drag and drop</span>
                <small>Supports: Images, PDFs, PowerPoint, Word documents</small>
              </div>
            </label>
          </div>
        )}
        
        {files.length > 0 && (
          <div className="uploaded-files">
            <h5>Uploaded Files ({files.length})</h5>
            <div className="files-grid">
              {files.map((file) => (
                <div key={file.id} className="file-item">
                  <div className="file-preview-container">
                    {file.preview ? (
                      <img src={file.preview} alt={file.originalName} className="file-preview" />
                    ) : (
                      <div className="file-icon">
                        {getFileTypeIcon(file.mimeType)}
                      </div>
                    )}
                    <div className="file-overlay">
                      <button 
                        className="preview-button"
                        onClick={() => handleFilePreview(file, tabNumber)}
                        title="Preview file"
                      >
                        👁️ Preview
                      </button>
                    </div>
                  </div>
                  <div className="file-info">
                    <div className="file-name">{file.originalName}</div>
                    <div className="file-meta">
                      <span className="file-size">{(file.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                      <span className="file-type">{getFileTypeName(file.mimeType)}</span>
                    </div>
                  </div>
                  <div className="file-actions">
                    <a 
                      href={file.downloadURL} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="download-link"
                      title="Download file"
                    >
                      ⬇️
                    </a>
                    <button 
                      className="remove-file"
                      onClick={() => removeFile(file.id, tabNumber)}
                      title="Remove file"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Add global mouse event listeners for image divider dragging
  React.useEffect(() => {
    if (isDraggingImageDivider) {
      const handleMouseMove = (e) => {
        const container = document.querySelector('[data-image-resizable-container]');
        if (container) {
          const containerWidth = container.offsetWidth;
          const newLeftWidth = (e.clientX / containerWidth) * 100;
          const constrainedWidth = Math.max(30, Math.min(80, newLeftWidth));
          setImageLeftWidth(constrainedWidth);
        }
      };

      const handleMouseUp = () => {
        setIsDraggingImageDivider(false);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDraggingImageDivider]);

  // Add keyboard navigation for carousel
  React.useEffect(() => {
    if (carouselPreviewOpen) {
      const handleKeyDown = (e) => {
        switch (e.key) {
          case 'ArrowLeft':
            e.preventDefault();
            setCarouselActiveIndex(prev => 
              prev > 0 ? prev - 1 : carouselFiles.length - 1
            );
            break;
          case 'ArrowRight':
            e.preventDefault();
            setCarouselActiveIndex(prev => 
              prev < carouselFiles.length - 1 ? prev + 1 : 0
            );
            break;
          case 'Escape':
            setCarouselPreviewOpen(false);
            break;
          default:
            break;
        }
      };

      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [carouselPreviewOpen, carouselFiles.length]);

  // Handle carousel navigation
  const handleCarouselSelect = (selectedIndex) => {
    setCarouselActiveIndex(selectedIndex);
  };

  // Open carousel preview for tab3
  const openCarouselPreview = async (tabNumber) => {
    if (tab3Files.length === 0) {
      toast.info('No files to preview');
      return;
    }
    
    setCarouselLoading(true);
    try {
      // Small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 300));
      
      setCarouselFiles(tab3Files);
      setCarouselPreviewType('tab3');
      setCarouselActiveIndex(0);
      setCarouselPreviewOpen(true);
    } catch (error) {
      console.error('Error opening carousel preview:', error);
      toast.error('Failed to open preview');
    } finally {
      setCarouselLoading(false);
    }
  };

  // Get file preview content based on file type
  const getFilePreviewContent = (file) => {
    if (file.mimeType.startsWith('image/')) {
      return (
        <img
          className="d-block w-100"
          src={file.downloadURL}
          alt={file.originalName}
          style={{ maxHeight: '60vh', objectFit: 'contain' }}
        />
      );
    } else if (file.mimeType.includes('pdf')) {
      return (
        <div className="pdf-preview-container">
          <iframe
            src={file.downloadURL}
            title={file.originalName}
            width="100%"
            height="60vh"
            style={{ border: 'none', borderRadius: '8px' }}
          />
        </div>
      );
    } else if (file.mimeType.includes('powerpoint') || file.mimeType.includes('presentation')) {
      return (
        <div className="powerpoint-preview-container">
          <iframe
            src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(file.downloadURL)}`}
            width="100%"
            height="100%"
            frameBorder="0"
            title={file.originalName}
            style={{ border: 'none', borderRadius: '8px' }}
          />
        </div>
      );
    } else if (file.mimeType.includes('word') || file.mimeType.includes('document')) {
      return (
        <div className="document-preview-container">
          <div className="document-icon">📄</div>
          <h4>Document Preview</h4>
          <p>{file.originalName}</p>
          <div className="document-actions">
            <a 
              href={file.downloadURL}
              target="_blank"
              rel="noopener noreferrer"
              className="download-document-btn"
            >
              Download Document
            </a>
            <button 
              className="view-document-btn"
              onClick={() => window.open(file.downloadURL, '_blank')}
            >
              Open in New Tab
            </button>
          </div>
        </div>
      );
    } else {
      return (
        <div className="generic-file-preview-container">
          <div className="file-icon">📁</div>
          <h4>File Preview</h4>
          <p>{file.originalName}</p>
          <p className="file-type">{getFileTypeName(file.mimeType)}</p>
          <a 
            href={file.downloadURL}
            target="_blank"
            rel="noopener noreferrer"
            className="download-generic-btn"
          >
            Download File
          </a>
        </div>
      );
    }
  };

  // Get file type icon based on MIME type
  const getFileTypeIcon = (mimeType) => {
    if (mimeType.startsWith('image/')) {
      return '🖼️';
    } else if (mimeType.includes('pdf')) {
      return '📕';
    } else if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) {
      return '📊';
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return '📄';
    } else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
      return '📈';
    } else if (mimeType.includes('text/')) {
      return '📝';
    } else if (mimeType.includes('video/')) {
      return '🎥';
    } else if (mimeType.includes('audio/')) {
      return '🎵';
    } else {
      return '📁';
    }
  };

  // Get file type display name
  const getFileTypeName = (mimeType) => {
    if (mimeType.startsWith('image/')) {
      return 'Image';
    } else if (mimeType.includes('pdf')) {
      return 'PDF Document';
    } else if (mimeType.includes('powerpoint') || mimeType.includes('presentation')) {
      return 'PowerPoint Presentation';
    } else if (mimeType.includes('word') || mimeType.includes('document')) {
      return 'Word Document';
    } else if (mimeType.includes('excel') || mimeType.includes('spreadsheet')) {
      return 'Excel Spreadsheet';
    } else if (mimeType.includes('text/')) {
      return 'Text Document';
    } else if (mimeType.includes('video/')) {
      return 'Video File';
    } else if (mimeType.includes('audio/')) {
      return 'Audio File';
    } else {
      return 'File';
    }
  };

  // Generate charts data based on all images in the selected session(s)
  const generateJourneyViolationsChart = () => {
    if (!availableDemoImages || availableDemoImages.length === 0) return [];
    
    const journeyCounts = {};
    
    availableDemoImages.forEach(image => {
      const journeyName = image.journeyName || 'Unknown Journey';
      if (!journeyCounts[journeyName]) {
        journeyCounts[journeyName] = 0;
      }
      
      // Count violations/DPs from image response
      if (image.response && Array.isArray(image.response)) {
        const violations = image.response.filter(outcome => 
          outcome.confirmedOutcome && 
          (outcome.type === 'violation' || outcome.type === 'dark_pattern' || outcome.type === 'outcome')
        );
        journeyCounts[journeyName] += violations.length;
      }
    });
    
    // Sort by count descending and calculate percentages
    const sortedEntries = Object.entries(journeyCounts)
      .sort(([,a], [,b]) => b - a)
      .map(([journey, count]) => ({
        journey,
        count,
        percentage: availableDemoImages.length > 0 ? ((count / availableDemoImages.length) * 100).toFixed(1) : '0.0'
      }));
    
    return sortedEntries;
  };

  /**
   * Get images and their violation details for a specific journey
   * @param {string} journeyName - The name of the journey to get images for
   * @returns {Array} Array of images with their violation counts and details
   */
  const getJourneyImageDetails = (journeyName) => {
    if (!availableDemoImages || availableDemoImages.length === 0) return [];
    
    // Filter images for the specific journey
    const journeyImages = availableDemoImages.filter(image => 
      (image.journeyName || 'Unknown Journey') === journeyName
    );
    
    // Map each image with its violation details
    return journeyImages.map(image => {
      const violations = [];
      
      if (image.response && Array.isArray(image.response)) {
        image.response.forEach(outcome => {
          if (outcome.confirmedOutcome && 
              (outcome.type === 'violation' || outcome.type === 'dark_pattern' || outcome.type === 'outcome')) {
            violations.push({
              type: outcome.type,
              title: outcome.title || 'Untitled Violation',
              description: outcome.description || '',
              regulation: outcome.regulation || 'Unknown Regulation'
            });
          }
        });
      }
      
      return {
        id: image.id,
        imageName: image.imageName || `Image ${image.id}`,
        imageUrl: image.imageUrl,
        violationCount: violations.length,
        violations: violations,
        sessionName: image.sessionName || 'Unknown Session'
      };
    }).sort((a, b) => b.violationCount - a.violationCount); // Sort by violation count descending
  };

  /**
   * Toggle the expanded state of a journey in the chart
   * @param {string} journeyName - The name of the journey to toggle
   */
  const toggleJourneyExpansion = (journeyName) => {
    setExpandedJourneys(prev => ({
      ...prev,
      [journeyName]: !prev[journeyName]
    }));
  };

  /**
   * Generates violation types chart data by extracting type information from laws array
   * @returns {Array} Array of violation type objects with count and percentage
   */
  const generateViolationTypesChart = () => {
    if (!availableDemoImages || availableDemoImages.length === 0) return [];
    
    const violationTypeCounts = {};
    
    availableDemoImages.forEach(image => {
      if (image.response && Array.isArray(image.response)) {
        image.response.forEach(outcome => {
          // Check if outcome has confirmed status and laws array
          if (outcome.confirmedOutcome && outcome.laws && Array.isArray(outcome.laws)) {
            outcome.laws.forEach(law => {
              // Extract type from each law object
              if (law.type) {
                const type = law.type;
                if (!violationTypeCounts[type]) {
                  violationTypeCounts[type] = 0;
                }
                violationTypeCounts[type]++;
              }
            });
          }
        });
      }
    });
    
    // Sort by count descending and calculate percentages
    const sortedEntries = Object.entries(violationTypeCounts)
      .sort(([,a], [,b]) => b - a)
      .map(([type, count]) => ({
        type,
        count,
        percentage: availableDemoImages.length > 0 ? ((count / availableDemoImages.length) * 100).toFixed(1) : '0.0'
      }));
    
    return sortedEntries;
  };

  const generateRegulationsChart = () => {
    if (!availableDemoImages || availableDemoImages.length === 0) return [];
    
    const regulationCounts = {};
    
    availableDemoImages.forEach(image => {
      if (image.response && Array.isArray(image.response)) {
        image.response.forEach(outcome => {
          if (outcome.laws && Array.isArray(outcome.laws)) {
            outcome.laws.forEach(law => {
              const regulationName = law.law_name || law.law || 'Unknown Regulation';
              if (!regulationCounts[regulationName]) {
                regulationCounts[regulationName] = 0;
              }
              regulationCounts[regulationName]++;
            });
          }
        });
      }
    });
    
    // Sort by count descending and calculate percentages
    const sortedEntries = Object.entries(regulationCounts)
      .sort(([,a], [,b]) => b - a)
      .map(([regulation, count]) => ({
        regulation,
        count,
        percentage: availableDemoImages.length > 0 ? ((count / availableDemoImages.length) * 100).toFixed(1) : '0.0'
      }));
    
    return sortedEntries;
  };

  // Render empty state for charts
  const renderEmptyChartState = (message) => (
    <div className="empty-chart-state">
      <div className="empty-icon">📊</div>
      <h5>No Data Available</h5>
      <p>{message}</p>
    </div>
  );

  // Render synthesis sub-tabs
  const renderSynthesisSubTabs = () => {
    const journeyData = generateJourneyViolationsChart();
    const violationTypeData = generateViolationTypesChart();
    const regulationData = generateRegulationsChart();
    
    return (
      <div className="synthesis-sub-tabs">
        <div className="sub-tab-slider">
          <button 
            className={`sub-tab-button ${synthesisActiveSubTab === 'issues' ? 'active' : ''}`}
            onClick={() => setSynthesisActiveSubTab('issues')}
          >
            High Level Issues
          </button>
          <button 
            className={`sub-tab-button ${synthesisActiveSubTab === 'examples' ? 'active' : ''}`}
            onClick={() => setSynthesisActiveSubTab('examples')}
          >
            Examples
          </button>
        </div>
        
        <div className="sub-tab-content">
          {synthesisActiveSubTab === 'issues' ? (
            <div className="high-level-issues">
              
              {/* Summary Statistics */}
              <div className="summary-stats">
                <div className="stat-item">
                  <div className="stat-number">{availableDemoImages.length}</div>
                  <div className="stat-label">Total Images</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{journeyData.length}</div>
                  <div className="stat-label">Journeys</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{violationTypeData.length}</div>
                  <div className="stat-label">Violation Types</div>
                </div>
                <div className="stat-item">
                  <div className="stat-number">{regulationData.length}</div>
                  <div className="stat-label">Regulations</div>
                </div>
              </div>
              
              {/* Journey Violations Chart */}
              <div className="chart-section">
                <h5>Journey Violations Distribution</h5>
                <div className="chart-container">
                  {journeyData.length > 0 ? (
                    journeyData.map((item, index) => {
                      const isExpanded = expandedJourneys[item.journey];
                      const journeyImages = getJourneyImageDetails(item.journey);
                      
                      return (
                        <div key={index} className="chart-item expandable">
                          <div 
                            className="chart-item-header" 
                            onClick={() => toggleJourneyExpansion(item.journey)}
                            style={{ cursor: 'pointer' }}
                          >
                            <div className="chart-bar">
                              <div 
                                className="chart-bar-fill" 
                                style={{ width: `${Math.min(parseFloat(item.percentage), 100)}%` }}
                              ></div>
                            </div>
                            <div className="chart-label">
                              <span className="journey-name">{item.journey}</span>
                              <span className="violation-count">{item.count} violations</span>
                              <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                            </div>
                          </div>
                          
                          {isExpanded && (
                            <div className="journey-details">
                              <div className="journey-images-header">
                                <h6>Images/Violation ({journeyImages.length})</h6>
                              </div>
                              <div className="journey-images-grid">
                                {journeyImages.length > 0 ? (
                                  journeyImages.map((imageDetail, imgIndex) => (
                                    <div key={imgIndex} className="journey-image-item-simple">
                                      <div className="image-thumbnail-container">
                                        <img 
                                          src={imageDetail.imageUrl} 
                                          alt={imageDetail.imageName}
                                          className="journey-image-thumbnail"
                                          onClick={() => {
                                            setSelectedImageForView(availableDemoImages.find(img => img.id === imageDetail.id));
                                            setIsImageViewOpen(true);
                                          }}
                                        />
                                        <div className="violation-badge">
                                          {imageDetail.violationCount}
                                        </div>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="no-images-in-journey">
                                    No images found for this journey
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    renderEmptyChartState("No journey violations data available. Please select images with confirmed outcomes.")
                  )}
                </div>
              </div>
              
              {/* Violation Types Chart */}
              <div className="chart-section">
                <h5>Violation Types Distribution</h5>
                <div className="chart-container">
                  {violationTypeData.length > 0 ? (
                    violationTypeData.map((item, index) => (
                      <div key={index} className="chart-item">
                        <div className="chart-bar">
                          <div 
                            className="chart-bar-fill violation-type" 
                            style={{ width: `${Math.min(parseFloat(item.percentage), 100)}%` }}
                          ></div>
                        </div>
                        <div className="chart-label">
                          <span className="violation-type-name">{item.type}</span>
                          <span className="violation-count">{item.count} occurrences</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmptyChartState("No violation types data available. Please select images with confirmed outcomes.")
                  )}
                </div>
              </div>
              
              {/* Regulations Chart */}
              <div className="chart-section">
                <h5>Regulations Violations Distribution</h5>
                <div className="chart-container">
                  {regulationData.length > 0 ? (
                    regulationData.map((item, index) => (
                      <div key={index} className="chart-item">
                        <div className="chart-bar">
                          <div 
                            className="chart-bar-fill regulation" 
                            style={{ width: `${Math.min(parseFloat(item.percentage), 100)}%` }}
                          ></div>
                        </div>
                        <div className="chart-label">
                          <span className="regulation-name">{item.regulation}</span>
                          <span className="violation-count">{item.count} violations</span>
                        </div>
                      </div>
                    ))
                  ) : (
                    renderEmptyChartState("No regulations data available. Please select images with associated laws.")
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="examples-view">
              {renderImageSelection()}
            </div>
          )}
        </div>
      </div>
    );
  };

  // Handle view button click
  const handleViewClick = () => {
    setIsViewMode(true);
  };

  // Handle back button click
  const handleBackClick = () => {
    setIsViewMode(false);
  };

  // Handle edit mode toggle
  const toggleEditMode = () => {
    if (isEditMode) {
      // Exiting edit mode - reset editable data to original
      setEditableResearchData(JSON.parse(JSON.stringify(researchData)));
      setIsEditControlsExpanded(false); // Collapse controls when exiting edit mode
    }
    setIsEditMode(!isEditMode);
  };

  // Handle edit controls expansion toggle
  const toggleEditControls = () => {
    setIsEditControlsExpanded(!isEditControlsExpanded);
  };

  // Save edited research data
  const saveEditedResearchData = async () => {
    if (!selectedEnvironment || !editableResearchData) {
      toast.error('No data to save');
      return;
    }

    setIsSavingChanges(true);
    try {
      // Update both local state and Firebase
      setAnalysisResults(editableResearchData);
      setResearchData(editableResearchData);

      // Update Firebase with the edited data
      const environmentRef = doc(db, 'demo-environments', selectedEnvironment.id);
      await updateDoc(environmentRef, {
        analysisResults: cleanDataForFirebase(editableResearchData),
        lastUpdated: new Date()
      });

      toast.success('Research analysis updated successfully!');
      setIsEditMode(false);
      
      // Reload environments to get updated data
      await loadDemoEnvironments();
    } catch (error) {
      console.error('Error saving edited research data:', error);
      toast.error(`Failed to save changes: ${error.message}`);
    } finally {
      setIsSavingChanges(false);
    }
  };

  // Add new law row
  const addLawRow = () => {
    if (!editableResearchData?.laws) return;
    
    const newLaw = {
      name: "New Law",
      jurisdiction: "New Jurisdiction",
      key_provisions: ["New provision"],
      typical_violations: ["New violation"],
      penalty_range: "New penalty range"
    };
    
    setEditableResearchData(prev => ({
      ...prev,
      laws: [...prev.laws, newLaw]
    }));
  };

  // Remove law row
  const removeLawRow = (index) => {
    if (!editableResearchData?.laws) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      laws: prev.laws.filter((_, i) => i !== index)
    }));
  };

  // Update law field
  const updateLawField = (index, field, value) => {
    if (!editableResearchData?.laws) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      laws: prev.laws.map((law, i) => 
        i === index ? { ...law, [field]: value } : law
      )
    }));
  };

  // Add provision to law
  const addProvisionToLaw = (lawIndex) => {
    if (!editableResearchData?.laws) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      laws: prev.laws.map((law, i) => 
        i === lawIndex 
          ? { ...law, key_provisions: [...law.key_provisions, "New provision"] }
          : law
      )
    }));
  };

  // Remove provision from law
  const removeProvisionFromLaw = (lawIndex, provisionIndex) => {
    if (!editableResearchData?.laws) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      laws: prev.laws.map((law, i) => 
        i === lawIndex 
          ? { ...law, key_provisions: law.key_provisions.filter((_, j) => j !== provisionIndex) }
          : law
      )
    }));
  };

  // Update provision text
  const updateProvisionText = (lawIndex, provisionIndex, text) => {
    if (!editableResearchData?.laws) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      laws: prev.laws.map((law, i) => 
        i === lawIndex 
          ? { 
              ...law, 
              key_provisions: law.key_provisions.map((provision, j) => 
                j === provisionIndex ? text : provision
              )
            }
          : law
      )
    }));
  };

  // Add violation to law
  const addViolationToLaw = (lawIndex) => {
    if (!editableResearchData?.laws) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      laws: prev.laws.map((law, i) => 
        i === lawIndex 
          ? { ...law, typical_violations: [...law.typical_violations, "New violation"] }
          : law
      )
    }));
  };

  // Remove violation from law
  const removeViolationFromLaw = (lawIndex, violationIndex) => {
    if (!editableResearchData?.laws) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      laws: prev.laws.map((law, i) => 
        i === lawIndex 
          ? { ...law, typical_violations: law.typical_violations.filter((_, j) => j !== violationIndex) }
          : law
      )
    }));
  };

  // Update violation text
  const updateViolationText = (lawIndex, violationIndex, text) => {
    if (!editableResearchData?.laws) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      laws: prev.laws.map((law, i) => 
        i === lawIndex 
          ? { 
              ...law, 
              typical_violations: law.typical_violations.map((violation, j) => 
                j === violationIndex ? text : violation
              )
            }
          : law
      )
    }));
  };

  // Add new enforcement trend row
  const addEnforcementTrendRow = () => {
    if (!editableResearchData?.enforcement_trends) return;
    
    const newTrend = {
      jurisdiction: "New Jurisdiction",
      authority: "New Authority",
      emerging_enforcement_focus: ["New focus area"],
      recent_case_examples: [{
        case: "New Case",
        year: new Date().getFullYear(),
        amount: "Amount TBD",
        details: "Case details",
        source_link: ""
      }],
      risk_level: "Medium"
    };
    
    setEditableResearchData(prev => ({
      ...prev,
      enforcement_trends: [...prev.enforcement_trends, newTrend]
    }));
  };

  // Remove enforcement trend row
  const removeEnforcementTrendRow = (index) => {
    if (!editableResearchData?.enforcement_trends) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      enforcement_trends: prev.enforcement_trends.filter((_, i) => i !== index)
    }));
  };

  // Update enforcement trend field
  const updateEnforcementTrendField = (index, field, value) => {
    if (!editableResearchData?.enforcement_trends) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      enforcement_trends: prev.enforcement_trends.map((trend, i) => 
        i === index ? { ...trend, [field]: value } : trend
      )
    }));
  };

  // Add insight
  const addInsight = () => {
    if (!editableResearchData?.insights_for_company?.key_insights) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      insights_for_company: {
        ...prev.insights_for_company,
        key_insights: [...prev.insights_for_company.key_insights, "New insight"]
      }
    }));
  };

  // Remove insight
  const removeInsight = (index) => {
    if (!editableResearchData?.insights_for_company?.key_insights) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      insights_for_company: {
        ...prev.insights_for_company,
        key_insights: prev.insights_for_company.key_insights.filter((_, i) => i !== index)
      }
    }));
  };

  // Update insight text
  const updateInsightText = (index, text) => {
    if (!editableResearchData?.insights_for_company?.key_insights) return;
    
    setEditableResearchData(prev => ({
      ...prev,
      insights_for_company: {
        ...prev.insights_for_company,
        key_insights: prev.insights_for_company.key_insights.map((insight, i) => 
          i === index ? text : insight
        )
      }
    }));
  };

  // Helper function to ensure URLs have proper protocol
  const ensureValidUrl = (url) => {
    if (!url || typeof url !== 'string') return url;
    
    // If URL already has protocol, return as is
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    
    // If URL starts with www. or looks like a domain, add https://
    if (url.startsWith('www.') || url.includes('.')) {
      return `https://${url}`;
    }
    
    // For other cases, return as is (might be a relative path)
    return url;
  };

  /**
   * Check if a URL is a TikTok video URL
   * @param {string} url - The URL to check
   * @returns {boolean} True if the URL is a TikTok video URL
   */
  const isTikTokUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    return url.includes('tiktok.com') && url.includes('/video/');
  };

  /**
   * Get TikTok embed URL from video URL
   * @param {string} url - The TikTok video URL
   * @returns {string|null} The embed URL or null if invalid
   */
  const getTikTokEmbedUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    const match = url.match(/\/video\/(\d+)/);
    if (!match) return null;
    return `https://www.tiktok.com/embed/v2/${match[1]}`;
  };

  return (
    <div className="demo-container">
      {/* Demo Environment Selection/Creation */}
      {!isViewMode && (
        <div className="environment-section">
          <div className="environment-controls">
            <div className="environment-select">
              <label>Demo Environment:</label>
              <select 
                value={selectedEnvironment?.id || ''} 
                onChange={async (e) => {
                  const env = demoEnvironments.find(env => env.id === e.target.value);
                  
                                   if (env) {
                   // Create a copy of the environment to avoid mutating the original
                   const envCopy = { ...env };
                   
                   // Clean the environment's sessions before setting it
                   if (envCopy.selectedSessions) {
                     envCopy.selectedSessions = getValidSessions(envCopy.selectedSessions);
                   }
                   
                   setSelectedEnvironment(envCopy);
                   
                   // Reset view mode when selecting a new environment
                   setIsViewMode(false);
                   
                   // Load previously saved demo data if exists
                   await loadSavedDemo(envCopy.id);
                 } else {
                   // Clear all data if no environment selected
                   setSelectedEnvironment(null);
                   setUseCaseContext('');
                   setAnalysisResults(null);
                   setResearchData(null);
                   setSelectedDemoImages([]);
                       setTab3Files([]);
                   setSelectedSessionsSafely([]); // Clear sessions safely
                   setActiveTab('laws'); // Reset to first tab
                   setIsViewMode(false); // Reset view mode
                 }
                }}
              >
                <option value="">Select an environment</option>
                {demoEnvironments.map(env => (
                  <option key={env.id} value={env.id}>{env.name}</option>
                ))}
              </select>
            </div>
            
            <button 
              className="create-env-btn"
              onClick={() => setIsCreatingEnvironment(!isCreatingEnvironment)}
            >
              <div className="btn-icon">
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 1V7M1 4H7" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="btn-text">
                {isCreatingEnvironment ? 'Cancel' : 'New Environment'}
              </span>
            </button>
          </div>

          {isCreatingEnvironment && (
            <div className="create-environment-form">
              <div className="form-group">
                <label>Name Your Environment: <span className="required-asterisk">*</span></label>
                <input
                  type="text"
                  value={newEnvironmentName}
                  onChange={(e) => setNewEnvironmentName(e.target.value)}
                  placeholder="Enter environment name"
                />
              </div>
              <div className="form-group">
                <label>Environment Description: <span className="required-asterisk">*</span></label>
                <textarea
                  value={newEnvironmentDescription}
                  onChange={(e) => setNewEnvironmentDescription(e.target.value)}
                  placeholder="Enter environment description"
                />
              </div>
              
              {/* Session Selection */}
              <div className="form-group">
                <label>Select Sessions: <span className="required-asterisk">*</span></label>
                <p className="session-selection-help">
                  Only sessions with images in the demo-images collection are shown below.
                </p>
                <div className="session-selection-container">
                  {availableSessions.map((session) => {
                    const isSelected = selectedSessions.some(selected => selected.id === session.id);
                    return (
                      <div 
                        key={session.id} 
                        className={`session-selection-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedSessionsSafely(prev => {
                              const newSessions = prev.filter(s => s.id !== session.id);
                              console.log('Session deselected:', session.id, 'New sessions:', newSessions);
                              return newSessions;
                            });
                          } else {
                            // Only add valid sessions
                            if (session && session.id && session.sessionName) {
                              // Additional validation to ensure session data integrity
                              const validSession = {
                                id: session.id.trim(),
                                sessionName: session.sessionName.trim(),
                                description: session.description || ''
                              };
                              
                              setSelectedSessionsSafely(prev => {
                                const newSessions = [...prev, validSession];
                                console.log('Session selected:', validSession.id, 'New sessions:', newSessions);
                                return newSessions;
                              });
                            } else {
                              console.error('Invalid session data:', session);
                              toast.error('Invalid session data. Please try again.');
                            }
                          }
                        }}
                      >
                        <div className="session-checkbox">
                          {isSelected ? '☑️' : '☐'}
                        </div>
                        <div className="session-info">
                          <div className="session-name">{session.sessionName || session.id}</div>
                          {session.description && (
                            <div className="session-description">{session.description}</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                {availableSessions.length === 0 && (
                  <p className="no-sessions-message">No sessions with images available. Please add images to sessions first.</p>
                )}
              </div>
              
              <button 
                className="save-env-btn"
                onClick={createDemoEnvironment}
                disabled={!newEnvironmentName.trim() || !newEnvironmentDescription.trim() || selectedSessions.length === 0}
              >
                <div className="btn-content">
                  {isCreatingEnvironment ? 'Creating Environment' : 'Create Environment'}
                </div>
              </button>
            </div>
          )}
        </div>
      )}

      {/* View Mode Header with Back Button and Actions */}
      {isViewMode && selectedEnvironment && (
        <div className="view-mode-header">
          <div className="view-mode-controls">
            <button 
              className="back-btn"
              onClick={handleBackClick}
            >
              ← Back
            </button>
            <div className="environment-info">
              <h3>{selectedEnvironment.name}</h3>
              {/* <p>{selectedEnvironment.description}</p> */}
            </div>
            
            {/* Actions Dropdown in Header */}
            <div className="header-actions">
              <button 
                className="actions-toggle-btn"
                onClick={toggleEditControls}
                title={isEditControlsExpanded ? 'Hide Actions' : 'Show Actions'}
              >
                <span className="actions-text">Actions</span>
                <span className={`dropdown-arrow ${isEditControlsExpanded ? 'expanded' : ''}`}>
                  ▼
                </span>
              </button>
            </div>
          </div>

          {/* Collapsible Actions Controls */}
          <div className={`header-actions-dropdown ${isEditControlsExpanded ? 'expanded' : 'collapsed'}`}>
            <div className="edit-controls-content">                
              <div className="actions-grid">
                {/* Save Demo Section */}
                <div className="action-section">
                  <button 
                    className="save-demo-btn"
                    onClick={saveDemo}
                    disabled={isSaving}
                  >
                    {isSaving ? '💾 Saving Demo...' : '💾 Save Demo'}
                  </button>
                </div>

                {/* Research Analysis Section */}
                <div className="action-section">
                  <div className="edit-action-buttons">
                    {isEditMode ? (
                      <>
                        <button 
                          className="save-changes-btn"
                          onClick={saveEditedResearchData}
                          disabled={isSavingChanges}
                        >
                          {isSavingChanges ? '💾 Saving Changes...' : '💾 Save Changes'}
                        </button>
                        <button 
                          className="cancel-edit-btn"
                          onClick={toggleEditMode}
                          disabled={isSavingChanges}
                        >
                          ❌ Cancel Edit
                        </button>
                      </>
                    ) : (
                      <button 
                        className="edit-mode-btn"
                        onClick={toggleEditMode}
                      >
                        ✏️ Edit
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Use Case Analysis */}
      {selectedEnvironment && !isViewMode && (
        <div className="analysis-section">
          {/* Collapsible Use Case Context */}
          <div className="use-case-context-section">
            <button 
              className="context-toggle-btn"
              onClick={() => setIsUseCaseContextExpanded(!isUseCaseContextExpanded)}
            >
              <span className="toggle-icon">
                {isUseCaseContextExpanded ? '▼' : '▶'}
              </span>
              <span className="toggle-text">Use Case Scenario</span>
            </button>
            
            {isUseCaseContextExpanded && (
              <div className="context-form-container">
                <div className="form-group">
                  <textarea
                    value={useCaseContext}
                    onChange={(e) => setUseCaseContext(e.target.value)}
                    placeholder="Describe the use case context for analysis..."
                    rows="4"
                  />
                </div>
                <div className="button-group">
                  <button 
                    className="analyze-btn"
                    onClick={analyzeUseCase}
                    disabled={isAnalyzing || !useCaseContext.trim()}
                  >
                    {isAnalyzing ? 'Analyzing...' : 'Analyze'}
                  </button>
                  {analysisResults && (
                    <button 
                      className="view-btn"
                      onClick={handleViewClick}
                    >
                      View Results
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab Navigation */}
      {isViewMode && analysisResults && (
        <div className="tabs-container">
          <div className="tab-navigation">
            <button 
              className={`tab-button ${activeTab === 'laws' ? 'active' : ''}`}
              onClick={() => setActiveTab('laws')}
            >
              Regulatory Research
            </button>
            <button 
              className={`tab-button ${activeTab === 'issues' ? 'active' : ''}`}
              onClick={() => setActiveTab('issues')}
            >
              Synthesis
            </button>
            <button 
              className={`tab-button ${activeTab === 'tab3' ? 'active' : ''}`}
              onClick={() => setActiveTab('tab3')}
            >
              Value Proposition
            </button>
            <button 
              className={`tab-button ${activeTab === 'tab4' ? 'active' : ''}`}
              onClick={() => setActiveTab('tab4')}
            >
              Report
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'laws' && (
              <div className="tab-panel">
                {renderResearchCharts()}
              </div>
            )}

            {activeTab === 'issues' && (
              <div className="tab-panel">
                {renderSynthesisSubTabs()}
              </div>
            )}

            {activeTab === 'tab3' && (
              <div className="tab-panel">
                {renderFileUpload(3, tab3Files)}
              </div>
            )}

            {activeTab === 'tab4' && (
              <div className="tab-panel">
                {renderReportTab()}
              </div>
            )}
          </div>


        </div>
      )}

      {/* File Preview Modal */}
      {isImageViewOpen && selectedImageForView && (
        <div className="image-modal" onClick={() => setIsImageViewOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h3>{selectedImageForView.image_name}</h3>
                {selectedImageForView.sessionName && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => setImageViewMode('preview')}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: imageViewMode === 'preview' ? '#000EAB' : 'transparent',
                        color: imageViewMode === 'preview' ? '#fff' : '#000EAB',
                        border: '1px solid #000EAB',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Preview
                    </button>
                    <button
                      onClick={() => setImageViewMode('detailed')}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: imageViewMode === 'detailed' ? '#000EAB' : '#fff',
                        color: imageViewMode === 'detailed' ? '#fff' : '#000EAB',
                        border: '1px solid #000EAB',
                        borderRadius: '16px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Detailed Review
                    </button>
                  </div>
                )}
              </div>
              <button 
                className="close-button"
                onClick={() => setIsImageViewOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              {selectedImageForView.type === 'image' ? (
                // Image preview for uploaded files
                <div className="image-preview">
                  <img src={selectedImageForView.imageUrl} alt="Preview" />
                </div>
              ) : selectedImageForView.type === 'pdf' ? (
                // PDF preview with iFrame
                <div className="pdf-preview">
                  <h4>PDF Preview</h4>
                  <div className="pdf-iframe-container">
                    <iframe src={selectedImageForView.downloadURL} title={selectedImageForView.image_name} />
                  </div>
                </div>
              ) : selectedImageForView.type === 'powerpoint' ? (
                // PowerPoint preview
                <div className="powerpoint-preview">
                  <div className="powerpoint-icon">📊</div>
                  <h4>PowerPoint Presentation</h4>
                  <p>File: {selectedImageForView.image_name}</p>
                  <p>Path: {selectedImageForView.filePath}</p>
                  <div className="powerpoint-actions">
                    <a 
                      href={selectedImageForView.downloadURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-powerpoint-btn"
                    >
                      Download PowerPoint
                    </a>
                    <button 
                      className="view-powerpoint-btn"
                      onClick={() => window.open(selectedImageForView.downloadURL, '_blank')}
                    >
                      Open in New Tab
                    </button>
                  </div>
                </div>
              ) : selectedImageForView.type === 'document' ? (
                // Document preview (Word, Excel, etc.)
                <div className="document-preview">
                  <h4>Document Preview</h4>
                  <p>File: {selectedImageForView.image_name}</p>
                  <p>Path: {selectedImageForView.filePath}</p>
                  <div className="document-actions">
                    <a 
                      href={selectedImageForView.downloadURL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-document-btn"
                    >
                      Download Document
                    </a>
                    <button 
                      className="view-document-btn"
                      onClick={() => window.open(selectedImageForView.downloadURL, '_blank')}
                    >
                      Open in New Tab
                    </button>
                  </div>
                </div>
              ) : (
                // Demo image view with split screen option
                imageViewMode === 'detailed' ? (
                  // Detailed Review Split View
                  <div 
                    style={{ display: "flex", height: "60vh", position: "relative" }}
                    data-image-resizable-container
                  >
                    {/* Left side - Image/Video Preview */}
                    <div style={{ 
                      width: `${imageLeftWidth}%`, 
                      display: "flex", 
                      flexDirection: "column", 
                      borderRight: "1px solid #ddd" 
                    }}>
                      <div style={{
                        padding: '16px',
                        borderBottom: '1px solid #ddd',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                          {isTikTokUrl(selectedImageForView.imageUrl) ? 'Video Preview' : 'Image Preview'}
                        </h4>
                        <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                          {selectedImageForView.pageName || 'Unknown Page'}
                        </p>
                      </div>
                      
                      <div style={{ 
                        flex: 1, 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        overflow: 'hidden',
                        backgroundColor: '#f8f9fa',
                        padding: '16px'
                      }}>
                        {isTikTokUrl(selectedImageForView.imageUrl) ? (
                          <iframe
                            src={getTikTokEmbedUrl(selectedImageForView.imageUrl)}
                            title={selectedImageForView.image_name || 'TikTok Video'}
                            style={{
                              width: '100%',
                              maxWidth: '400px',
                              height: '100%',
                              minHeight: '600px',
                              border: 'none',
                              borderRadius: '8px',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                            }}
                            allowFullScreen
                            allow="autoplay; encrypted-media"
                            scrolling="no"
                          />
                        ) : (
                          <img
                            src={selectedImageForView.imageUrl}
                            alt="Screenshot"
                            style={{
                              maxWidth: '100%',
                              maxHeight: '100%',
                              objectFit: 'contain',
                              borderRadius: '8px',
                              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)'
                            }}
                          />
                        )}
                      </div>
                    </div>

                    {/* Resizable Divider */}
                    <div
                      style={{
                        width: '4px',
                        backgroundColor: isDraggingImageDivider ? '#000EAB' : '#ddd',
                        cursor: 'col-resize',
                        flexShrink: 0,
                        transition: isDraggingImageDivider ? 'none' : 'background-color 0.2s'
                      }}
                      onMouseDown={handleImageDividerMouseDown}
                    />

                    {/* Right side - Image Properties */}
                    <div style={{ 
                      width: `${100 - imageLeftWidth}%`, 
                      display: "flex", 
                      flexDirection: "column" 
                    }}>
                      <div style={{
                        padding: '16px',
                        borderBottom: '1px solid #ddd',
                        backgroundColor: '#f8f9fa'
                      }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '16px', fontWeight: '600' }}>
                          Image Properties
                        </h4>
                        <p style={{ margin: '0', fontSize: '14px', color: '#666' }}>
                          Detailed information and outcomes
                        </p>
                      </div>
                      
                      <div style={{ 
                        flex: 1, 
                        overflowY: "auto", 
                        padding: "16px",
                        backgroundColor: '#fff'
                      }}>
                        {/* Basic Properties */}
                        <div style={{ marginBottom: '20px' }}>
                          <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
                            Basic Information
                          </h5>
                          <div style={{ display: 'grid', gap: '8px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                              <span style={{ fontWeight: '500', color: '#666' }}>Session:</span>
                              <span style={{ color: '#333' }}>{selectedImageForView.sessionName}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                              <span style={{ fontWeight: '500', color: '#666' }}>Journey:</span>
                              <span style={{ color: '#333' }}>{selectedImageForView.journeyName}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                              <span style={{ fontWeight: '500', color: '#666' }}>Page:</span>
                              <span style={{ color: '#333' }}>{selectedImageForView.pageName || 'Unknown'}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0' }}>
                              <span style={{ fontWeight: '500', color: '#666' }}>URL:</span>
                              <span style={{ color: '#333', fontSize: '12px', maxWidth: '60%', wordBreak: 'break-all' }}>
                                {selectedImageForView.url || 'N/A'}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Outcomes Section */}
                        {selectedImageForView.response && selectedImageForView.response.length > 0 && (
                          <div style={{ marginBottom: '20px' }}>
                            <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
                              Confirmed Outcomes ({selectedImageForView.response.filter(r => r.confirmedOutcome).length})
                            </h5>
                            <div style={{ display: 'grid', gap: '12px' }}>
                              {selectedImageForView.response
                                .filter(outcome => outcome.confirmedOutcome)
                                .map((outcome, index) => (
                                  <div 
                                    key={index} 
                                    style={{
                                      padding: '12px',
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: '8px',
                                      border: '1px solid #e9ecef'
                                    }}
                                  >
                                    <div style={{ 
                                      display: 'flex', 
                                      justifyContent: 'space-between', 
                                      alignItems: 'center',
                                      marginBottom: '8px'
                                    }}>
                                      <span style={{
                                        backgroundColor: '#00b74a',
                                        color: '#fff',
                                        padding: '4px 8px',
                                        borderRadius: '12px',
                                        fontSize: '10px',
                                        fontWeight: '500'
                                      }}>
                                        {outcome.type || 'outcome'}
                                      </span>
                                      {outcome.dark_pattern && (
                                        <span style={{
                                          backgroundColor: '#F3E5F5',
                                          color: '#7B1FA2',
                                          padding: '4px 8px',
                                          borderRadius: '12px',
                                          fontSize: '10px',
                                          fontWeight: '500'
                                        }}>
                                          {outcome.dark_pattern}
                                        </span>
                                      )}
                                    </div>
                                    <p style={{ 
                                      margin: '0', 
                                      fontSize: '13px', 
                                      lineHeight: '1.4',
                                      color: '#333'
                                    }}>
                                      {outcome.text}
                                    </p>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}

                        {/* Laws Section */}
                        {selectedImageForView.response && selectedImageForView.response.length > 0 && (
                          <div>
                            <h5 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: '600', color: '#333' }}>
                              Associated Laws
                            </h5>
                            <div style={{ display: 'grid', gap: '12px' }}>
                              {selectedImageForView.response
                                .filter(outcome => outcome.laws && outcome.laws.length > 0)
                                .map((outcome, outcomeIndex) => (
                                  <div key={outcomeIndex} style={{ marginBottom: '16px' }}>
                                    <div style={{ 
                                      marginBottom: '8px',
                                      padding: '8px 12px',
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: '6px',
                                      border: '1px solid #e9ecef'
                                    }}>
                                      <p style={{ 
                                        margin: '0 0 8px 0', 
                                        fontSize: '12px', 
                                        lineHeight: '1.3',
                                        color: '#333',
                                        fontWeight: '500'
                                      }}>
                                        <strong>Outcome:</strong> {outcome.text}
                                      </p>
                                      {outcome.dark_pattern && (
                                        <span style={{
                                          backgroundColor: '#F3E5F5',
                                          color: '#7B1FA2',
                                          padding: '2px 6px',
                                          borderRadius: '8px',
                                          fontSize: '9px',
                                          fontWeight: '500'
                                        }}>
                                          {outcome.dark_pattern}
                                        </span>
                                      )}
                                    </div>
                                    
                                    <div style={{ display: 'grid', gap: '8px', marginLeft: '16px' }}>
                                      {outcome.laws.map((law, lawIndex) => (
                                        <div 
                                          key={lawIndex}
                                          style={{
                                            padding: '10px',
                                            backgroundColor: '#fff',
                                            borderRadius: '6px',
                                            border: '1px solid #e9ecef',
                                            borderLeft: '3px solid #000EAB'
                                          }}
                                        >
                                          <div style={{ 
                                            display: 'flex', 
                                            justifyContent: 'space-between', 
                                            alignItems: 'flex-start',
                                            marginBottom: '6px'
                                          }}>
                                            <span style={{
                                              backgroundColor: '#000EAB',
                                              color: '#fff',
                                              padding: '3px 8px',
                                              borderRadius: '12px',
                                              fontSize: '10px',
                                              fontWeight: '500'
                                            }}>
                                              {law.jurisdiction || 'N/A'}
                                            </span>
                                            <span style={{
                                              backgroundColor: '#28a745',
                                              color: '#fff',
                                              padding: '3px 8px',
                                              borderRadius: '12px',
                                              fontSize: '10px',
                                              fontWeight: '500'
                                            }}>
                                              {law.type || 'Added'}
                                            </span>
                                          </div>
                                          
                                          <div style={{ marginBottom: '6px' }}>
                                            <strong style={{ color: '#333', fontSize: '11px' }}>
                                              {law.law_name || law.law || 'Unknown Law'}
                                            </strong>
                                          </div>
                                          
                                          <div style={{ marginBottom: '6px' }}>
                                            <span style={{ color: '#666', fontSize: '10px' }}>
                                              <strong>Clause:</strong> {law.clause_name || law.clause || 'N/A'}
                                            </span>
                                          </div>
                                          
                                          {law.link && (
                                            <div style={{ marginTop: '8px' }}>
                                              <a 
                                                href={ensureValidUrl(law.link)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                  color: '#000EAB',
                                                  textDecoration: 'none',
                                                  fontSize: '10px',
                                                  display: 'inline-flex',
                                                  alignItems: 'center',
                                                  gap: '4px'
                                                }}
                                              >
                                                📖 View Passage
                                                <svg 
                                                  width="12" 
                                                  height="12" 
                                                  viewBox="0 0 24 24" 
                                                  fill="none" 
                                                  stroke="currentColor" 
                                                  strokeWidth="2"
                                                >
                                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                                  <polyline points="15,3 21,3 21,9"></polyline>
                                                  <line x1="10" y1="14" x2="21" y2="3"></line>
                                                </svg>
                                              </a>
                                            </div>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              
                              {/* Show message if no laws found */}
                              {!selectedImageForView.response.some(outcome => outcome.laws && outcome.laws.length > 0) && (
                                <div style={{
                                  padding: '20px',
                                  textAlign: 'center',
                                  color: '#666',
                                  backgroundColor: '#f8f9fa',
                                  borderRadius: '8px',
                                  border: '1px dashed #dee2e6'
                                }}>
                                  <p style={{ margin: '0', fontSize: '12px' }}>
                                    No laws have been associated with the outcomes yet.
                                  </p>
                                  <p style={{ margin: '4px 0 0 0', fontSize: '11px', color: '#999' }}>
                                    Laws can be added when viewing outcomes in the NewEditor.
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  // Simple preview mode
                  <div className="image-preview">
                    {isTikTokUrl(selectedImageForView.imageUrl) ? (
                      <iframe
                        src={getTikTokEmbedUrl(selectedImageForView.imageUrl)}
                        title={selectedImageForView.image_name || 'TikTok Video'}
                        style={{
                          width: '100%',
                          maxWidth: '605px',
                          minHeight: '700px',
                          margin: '0 auto',
                          display: 'block',
                          border: 'none',
                          borderRadius: '8px'
                        }}
                        allowFullScreen
                        allow="autoplay; encrypted-media"
                        scrolling="no"
                      />
                    ) : (
                      <img src={selectedImageForView.imageUrl} alt="Preview" />
                    )}
                  </div>
                )
              )}
              
              {/* Properties section for demo images in preview mode */}
              {selectedImageForView.sessionName && imageViewMode === 'preview' && (
                <div className="image-properties">
                  <h4>Image Properties</h4>
                  <div className="property-item">
                    <strong>Session:</strong> {selectedImageForView.sessionName}
                  </div>
                  <div className="property-item">
                    <strong>Journey:</strong> {selectedImageForView.journeyName}
                  </div>
                  <div className="property-item">
                    <strong>Page:</strong> {selectedImageForView.pageName}
                  </div>
                  <div className="property-item">
                    <strong>URL:</strong> {selectedImageForView.url}
                  </div>
                  {selectedImageForView.response && (
                    <div className="property-item">
                      <strong>Outcomes:</strong>
                      <ul>
                        {selectedImageForView.response.map((outcome, index) => (
                          <li key={index}>{outcome.text}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Carousel Preview Modal */}
      {carouselPreviewOpen && carouselFiles.length > 0 && (
        <div className="carousel-modal" onClick={() => setCarouselPreviewOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="header-content">
                <h3>
                  Value Proposition Files Preview
                </h3>
                <div className="file-counter">
                  {carouselActiveIndex + 1} of {carouselFiles.length}
                </div>
              </div>
              <button 
                className="close-button"
                onClick={() => setCarouselPreviewOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="carousel-container">
                <Carousel 
                  activeIndex={carouselActiveIndex} 
                  onSelect={handleCarouselSelect}
                  interval={null}
                  indicators={true}
                  controls={true}
                  fade={false}
                >
                  {carouselFiles.map((file, index) => (
                    <Carousel.Item key={file.id}>
                      <div className="carousel-item-content">
                        {getFilePreviewContent(file)}
                      </div>
                      <Carousel.Caption className="carousel-caption">
                        <div className="caption-content">
                          <h5>{file.originalName}</h5>
                          <div className="file-meta-info">
                            <span className="file-type-badge">{getFileTypeName(file.mimeType)}</span>
                            <span className="file-size-badge">
                              {(file.fileSize / 1024 / 1024).toFixed(2)} MB
                            </span>
                          </div>
                        </div>
                      </Carousel.Caption>
                    </Carousel.Item>
                  ))}
                </Carousel>
              </div>
              
              {/* File Navigation Panel */}
              <div className="file-navigation-panel">
                <div className="navigation-header">
                  <h4>All Files ({carouselFiles.length})</h4>
                  <button 
                    className="close-panel-btn"
                    onClick={() => setCarouselPreviewOpen(false)}
                  >
                    Close Preview
                  </button>
                </div>
                <div className="file-list">
                  {carouselFiles.map((file, index) => (
                    <div 
                      key={file.id} 
                      className={`file-list-item ${index === carouselActiveIndex ? 'active' : ''}`}
                      onClick={() => setCarouselActiveIndex(index)}
                    >
                      <div className="file-icon-small">
                        {getFileTypeIcon(file.mimeType)}
                      </div>
                      <div className="file-info-small">
                        <div className="file-name-small">{file.originalName}</div>
                        <div className="file-meta-small">
                          <span>{getFileTypeName(file.mimeType)}</span>
                          <span>{(file.fileSize / 1024 / 1024).toFixed(2)} MB</span>
                        </div>
                      </div>
                      <div className="file-actions-small">
                        <a 
                          href={file.downloadURL}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="download-small-btn"
                          title="Download file"
                        >
                          ⬇️
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Iframe Popup Modal */}
      {isIframePopupOpen && (
        <div className="iframe-modal" onClick={() => setIsIframePopupOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h3>Report Interface</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    onClick={() => window.open('https://report.fairpatterns.com/login', '_blank')}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: '#000EAB',
                      color: '#fff',
                      border: '1px solid #000EAB',
                      borderRadius: '16px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    New Tab
                  </button>
                </div>
              </div>
              <button 
                className="close-button"
                onClick={() => setIsIframePopupOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="iframe-preview-fullscreen">
                <iframe
                  src="https://report.fairpatterns.com/login"
                  title="Report Interface Full Screen"
                  style={{
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '8px'
                  }}
                  allowFullScreen
                  sandbox="allow-scripts allow-forms allow-popups allow-same-origin allow-top-navigation"
                  referrerPolicy="no-referrer"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />
    </div>
  );
};

export default Demo;
